import type { Comment, Reply } from '@getaccept/lib-shared/src/contextual-comments/types/comment';
import { CommentStatus } from '@getaccept/lib-shared/src/contextual-comments/types/comment-status';
import type { CommentParticipant } from '../types/comment-participant';
import type { CommentReadEvent } from '../types/comment-read-event';

export class CommentsHelper {
  static readonly TEMP_COMMENT_ID = 'temp comment id';

  static readEventsAreEqual(event1: CommentReadEvent, event2: CommentReadEvent) {
    return (
      event1.participant.userId === event2.participant.userId &&
      event1.participant.recipientId === event2.participant.recipientId &&
      event1.timestamp === event2.timestamp
    );
  }

  static addComment(comment: Comment, comments: Comment[]) {
    return [...comments, comment];
  }

  static removeComment(comments: Comment[], commentId: string) {
    return comments.filter(({ id }) => id !== commentId);
  }

  static updateComment(commentPartial: Partial<Comment>, commentId: string, comments: Comment[]) {
    return comments.map(comment =>
      comment.id === commentId ? { ...comment, ...commentPartial } : comment
    );
  }

  static addReply(reply: Reply, commentId: string, comments: Comment[]) {
    return comments.map((comment: Comment) => {
      if (comment.id !== commentId) {
        return comment;
      }
      if (comment.replies?.length) {
        return { ...comment, replies: [...comment.replies, reply] };
      }
      return { ...comment, replies: [reply] };
    });
  }

  static removeReply(replyId: string, commentId: string, comments: Comment[]) {
    return comments.map(comment =>
      comment.id === commentId
        ? { ...comment, replies: comment.replies.filter(({ id }) => id !== replyId) }
        : comment
    );
  }

  static updateReply(
    replyPartial: Partial<Reply>,
    replyId: string,
    commentId: string,
    comments: Comment[]
  ) {
    return comments.map(comment =>
      comment.id === commentId
        ? {
            ...comment,
            replies: comment.replies.map(reply =>
              reply.id === replyId ? { ...reply, ...replyPartial } : reply
            ),
          }
        : comment
    );
  }

  static addRead(readEvent: CommentReadEvent, commentId: string, comments: Comment[]) {
    return comments.map(comment =>
      comment.id === commentId ? { ...comment, reads: [...comment.reads, readEvent] } : comment
    );
  }

  static removeRead(readEvent: CommentReadEvent, commentId: string, comments: Comment[]) {
    return comments.map(comment =>
      comment.id === commentId
        ? {
            ...comment,
            reads: comment.reads.filter(
              event => !CommentsHelper.readEventsAreEqual(event, readEvent)
            ),
          }
        : comment
    );
  }

  static addReplyRead(
    readEvent: CommentReadEvent,
    replyId: string,
    commentId: string,
    comments: Comment[]
  ) {
    return comments.map(comment =>
      comment.id === commentId
        ? {
            ...comment,
            replies: comment.replies.map(reply =>
              reply.id === replyId ? { ...reply, reads: [...reply.reads, readEvent] } : reply
            ),
          }
        : comment
    );
  }

  static removeReplyRead(
    readEvent: CommentReadEvent,
    replyId: string,
    commentId: string,
    comments: Comment[]
  ) {
    return comments.map(comment =>
      comment.id === commentId
        ? {
            ...comment,
            replies: comment.replies.map(reply =>
              reply.id === replyId
                ? {
                    ...reply,
                    reads: reply.reads.filter(
                      event => !CommentsHelper.readEventsAreEqual(event, readEvent)
                    ),
                  }
                : reply
            ),
          }
        : comment
    );
  }

  static isGhostComment(comment: Comment) {
    return comment.editorSelection?.detachedFromContent;
  }

  static isReadByUser(reads: CommentReadEvent[], userId: string) {
    return reads.some(read => read.participant.userId === userId);
  }

  static isReadByRecipient(reads: CommentReadEvent[], recipientId: string) {
    return reads.some(read => read.participant.recipientId === recipientId);
  }

  static isViewableComment(comment: Comment, currentUserId: string) {
    const { editorSelection, status, createdBy } = comment;
    const validStatuses = [CommentStatus.Active, CommentStatus.Resolved];

    if (createdBy?.userId === currentUserId) {
      validStatuses.push(CommentStatus.Draft);
    }

    return !!editorSelection && validStatuses.includes(status);
  }

  static findComment(comments: Comment[], commentId: string) {
    return comments.find(({ id }) => id === commentId);
  }

  static commentExists(comments: Comment[], commentId: string) {
    return comments.some(({ id }) => id === commentId);
  }

  static replyExists(comments: Comment[], commentId: string, replyId: string): boolean {
    const comment = comments.find(({ id }) => id === commentId);
    return !!comment?.replies?.some(({ id }) => id === replyId);
  }

  static findReply(comments: Comment[], commentId: string, replyId: string) {
    const comment = CommentsHelper.findComment(comments, commentId);
    return comment ? comment.replies.find(({ id }) => id === replyId) : undefined;
  }

  private static getUnreadCommentsByUser(comments: Comment[], userId: string) {
    return comments.filter(({ reads }) => !CommentsHelper.isReadByUser(reads, userId));
  }

  static getUnreadCommentsByRecipient(comments: Comment[], recipientId: string) {
    return comments.filter(({ reads }) => !CommentsHelper.isReadByRecipient(reads, recipientId));
  }

  static getUnreadRepliesByUser(replies: Reply[], userId: string) {
    return replies.filter(({ reads }) => !CommentsHelper.isReadByUser(reads, userId));
  }

  static getUnreadRepliesByRecipient(replies: Reply[], recipientId: string) {
    return replies.filter(({ reads }) => !CommentsHelper.isReadByRecipient(reads, recipientId));
  }

  static getTotalUnreadCount(comments: Comment[], { userId, recipientId }: CommentParticipant) {
    if (userId) {
      const unreadCommentsCount = CommentsHelper.getUnreadCommentsByUser(comments, userId).length;
      const unreadRepliesCount = comments.flatMap(({ replies }) =>
        CommentsHelper.getUnreadRepliesByUser(replies, userId)
      ).length;

      return unreadCommentsCount + unreadRepliesCount;
    }

    if (recipientId) {
      const unreadCommentsCount = CommentsHelper.getUnreadCommentsByRecipient(comments, recipientId)
        .length;
      const unreadRepliesCount = comments.flatMap(({ replies }) =>
        CommentsHelper.getUnreadRepliesByRecipient(replies, recipientId)
      ).length;

      return unreadCommentsCount + unreadRepliesCount;
    }

    return 0;
  }

  static sortByCreatedAt(comments: Comment[], ascending: boolean) {
    return [...comments].sort((a, b) => {
      if (a.createdAt < b.createdAt) {
        return ascending ? -1 : 1;
      }
      if (a.createdAt > b.createdAt) {
        return ascending ? 1 : -1;
      }

      return 0;
    });
  }

  static commentAlreadyUpdated(
    comments: Comment[],
    commentId: string,
    data: Partial<Comment>
  ): boolean {
    const affectedComment = this.findComment(comments, commentId);
    if (!affectedComment) {
      return false;
    }
    return Object.keys(data).every((key: string) => affectedComment[key] === data[key]);
  }

  static filterCommentsBySection(comments: Comment[], sectionId: string) {
    return comments.filter(comment => comment.editorSelection?.sectionId === sectionId);
  }

  static filterCommentsByRow(comments: Comment[], rowId: string) {
    return comments.filter(comment => comment.editorSelection?.rowId === rowId);
  }

  static filterCommentsByCell(comments: Comment[], cellId: string) {
    return comments.filter(comment => comment.editorSelection?.cellId === cellId);
  }

  static filterCommentsByNode(comments: Comment[], nodeId: string) {
    return comments.filter(comment => comment.editorSelection?.nodeId === nodeId);
  }
}
